import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import ToolTip from "../../../../../parts/tooltip";
import type {
  BodyPartKey,
  TrackerKey,
} from "../../../../../types/clientConfig";
import { DashboardOptionsCard } from "./DashboardOptionsCard";
import { FeatureStatusCard } from "./FeatureStatusCard";
import { TrackingCard } from "./TrackingCard";
import { TrainingFocusCard } from "./TrainingFocusCard";
import {
  buildFormState,
  buildPayload,
  hasActiveMetric,
  isSameFormState,
  type PushDashboardFormState,
  type PushDashboardLoaded,
} from "./utils";

const PUSH_FORM_ID = "form-push";

/**
 * Daily Dashboard defaults of a PUSH coach (Settings › Preferences › PUSH). The values live in
 * React state; while they differ from what is saved, a hidden `pushDashboardDefaults` field carries
 * the object to save, so the page's Save button (a plain form POST) sends it only when edited.
 */
export const PushDailyDashboardDefaults = ({
  data,
  onFormChange,
}: {
  data: PushDashboardLoaded;
  onFormChange: () => void;
}) => {
  const { t } = useTranslation("dashboard");
  const { defaults, featureStatus, msbDefaults } = data;

  const initialState = useMemo(
    () => buildFormState(defaults, msbDefaults),
    [defaults, msbDefaults]
  );
  const [state, setState] = useState<PushDashboardFormState>(initialState);

  const dirty = !isSameFormState(state, initialState);
  const payload = JSON.stringify(buildPayload(state, msbDefaults));

  // Discard resets the form: bring the section back before the page snapshots the form
  useEffect(() => {
    const form = document.getElementById(PUSH_FORM_ID);
    const restore = () => flushSync(() => setState(initialState));
    form?.addEventListener("reset", restore);
    return () => form?.removeEventListener("reset", restore);
  }, [initialState]);

  // Buttons and chips are not form fields, so the page is told about each change explicitly
  const update = (
    updater: (current: PushDashboardFormState) => PushDashboardFormState
  ) => {
    setState(updater);
    onFormChange();
  };

  const setMetric = (key: TrackerKey, value: boolean) =>
    update((current) => ({
      ...current,
      metrics: { ...current.metrics, [key]: value },
    }));

  const setBodyParts = (bodyParts: BodyPartKey[]) =>
    update((current) => ({ ...current, bodyParts }));

  return (
    <section className="push-daily-dashboard" aria-labelledby="push-dd-title">
      {dirty && (
        <input type="hidden" name="pushDashboardDefaults" value={payload} />
      )}
      <header className="push-dd-header">
        <div>
          <h4 id="push-dd-title" className="push-dd-title">
            {t("pushDailyDashboard.title")}
          </h4>
          <p className="p2">{t("pushDailyDashboard.subtitle")}</p>
        </div>
        <div className="push-dd-header__actions">
          <button
            type="button"
            className="push-dd-text-button"
            onClick={() => update(() => buildFormState({}, msbDefaults))}
          >
            {t("pushDailyDashboard.reset")}
          </button>
          <ToolTip
            id="push-daily-dashboard-tooltip"
            body={t("pushDailyDashboard.tooltip")}
          >
            <SvgIcon
              alt={t("pushDailyDashboard.tooltipAlt")}
              className="cursor-pointer"
              src="/img/new-icons/question-circle.svg"
            />
          </ToolTip>
        </div>
      </header>
      <FeatureStatusCard featureStatus={featureStatus} />
      <DashboardOptionsCard
        state={state}
        canLog={hasActiveMetric(state)}
        onChange={(key, value) =>
          update((current) => ({ ...current, [key]: value }))
        }
      />
      <TrackingCard
        state={state}
        allBodyParts={msbDefaults.bodyParts}
        onMetricChange={setMetric}
        onBodyPartsChange={setBodyParts}
      />
      <TrainingFocusCard
        value={state.trainingFocus}
        onChange={(trainingFocus) =>
          update((current) => ({ ...current, trainingFocus }))
        }
      />
    </section>
  );
};
